import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import AffiliateBilling from '@/pages/affiliate/AffiliateBilling';
import IndividualBilling from '@/pages/individual/IndividualBilling';
import { useEffectiveRole } from '@/stores/authStore';
import { CreditCard } from 'lucide-react';

/**
 * Shared Billing page that renders the appropriate component based on user role.
 *
 * - Affiliate Owner: Uses AffiliateBilling
 * - Individual: Uses IndividualBilling
 * - Others: Shows "not applicable" message
 */
export default function Billing() {
  const effectiveRole = useEffectiveRole();

  // Affiliate owners manage gym subscription
  if (effectiveRole === 'affiliate_owner') {
    return <AffiliateBilling />;
  }

  // Individuals manage personal subscription
  if (effectiveRole === 'individual') {
    return <IndividualBilling />;
  }

  // Other roles don't have billing (members are covered by affiliate, agents/coaches don't pay)
  return (
    <>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gradient-gold">Billing</h1>
        <p className="mt-1 text-muted-foreground">Subscription and payment management.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Billing
          </CardTitle>
          <CardDescription>
            {effectiveRole === 'member' || effectiveRole === 'affiliate_coach'
              ? 'Your gym membership is managed by your affiliate.'
              : 'Billing is managed by account owners.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-12">
            <div className="rounded-full bg-muted p-4 mx-auto w-fit mb-4">
              <CreditCard className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold mb-2">Billing Not Applicable</h3>
            <p className="text-muted-foreground max-w-md mx-auto">
              {effectiveRole === 'member'
                ? 'Your access to ErgShoot is included with your gym membership. Contact your gym owner for billing inquiries.'
                : effectiveRole === 'affiliate_coach'
                  ? 'Billing is managed by the affiliate owner. Contact them for subscription details.'
                  : effectiveRole === 'agent'
                    ? 'As an agent, you earn commissions from affiliate subscriptions. View your earnings in the Commissions section.'
                    : effectiveRole === 'hq_admin'
                      ? 'HQ Admin accounts are managed internally. Contact the ErgShoot team for billing inquiries.'
                      : 'Billing is available to affiliate owners and individual subscribers.'}
            </p>
          </div>
        </CardContent>
      </Card>
    </>
  );
}
